import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { paymentStatus } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [amount, setAmount] = useState(null);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await paymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setAmount(res.amount);
          setState("success");
          clear();
          return;
        }
        if (res.payment_status === "expired" || res.status === "failed") {
          setState("error");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      attempts += 1;
      if (attempts > 10) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0D110D] flex items-center justify-center px-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-center max-w-lg"
      >
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-[#D47738] animate-spin mx-auto mb-8" />
            <h1 className="font-serif-display text-4xl text-[#E8E2D2]">
              Verifica del pagamento...
            </h1>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-[#4A6E4A] mx-auto mb-8" strokeWidth={1.5} />
            <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#D47738] mb-4">
              Ordine Confermato
            </p>
            <h1 className="font-serif-display text-5xl md:text-6xl text-[#E8E2D2] mb-6 leading-none">
              Grazie <span className="italic">di cuore</span>
            </h1>
            <p className="text-[#E8E2D2]/60 mb-2">
              Il tuo ordine è stato ricevuto. Ti abbiamo inviato una conferma.
            </p>
            {amount != null && (
              <p className="font-mono-accent text-[#D47738] mb-10">
                Totale: € {Number(amount).toFixed(2)}
              </p>
            )}
            <Link
              to="/"
              className="inline-block bg-[#D47738] text-[#0D110D] px-10 py-4 font-mono-accent text-xs uppercase tracking-[0.2em] hover:bg-[#E8E2D2] transition-colors duration-300"
              data-testid="back-home-btn"
            >
              Torna al Birrificio
            </Link>
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <XCircle className="w-16 h-16 text-[#9E3C3C] mx-auto mb-8" strokeWidth={1.5} />
            <h1 className="font-serif-display text-4xl text-[#E8E2D2] mb-6">
              {state === "timeout" ? "Pagamento in elaborazione" : "Qualcosa è andato storto"}
            </h1>
            <p className="text-[#E8E2D2]/60 mb-10">
              {state === "timeout"
                ? "Il pagamento richiede più tempo del previsto. Controlla la tua email."
                : "Non siamo riusciti a confermare il pagamento."}
            </p>
            <Link to="/" className="inline-block border border-white/20 text-[#E8E2D2] px-10 py-4 font-mono-accent text-xs uppercase tracking-[0.2em] hover:border-[#D47738] transition-colors duration-300">
              Torna alla Home
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
